import { QueryCache, QueryClient } from "@tanstack/react-query";
import { isRetryablePendingError, isServiceUnavailableError, SERVICE_UNAVAILABLE_MESSAGE } from "./lib/api-client.js";
import { customToast } from "./toast.js";
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 60 * 1000, // 1 minute default stale time
            gcTime: 5 * 60 * 1000, // 5 minutes default garbage collection time
            retry: (failureCount, error) => {
                if (isRetryablePendingError(error)) {
                    return true;
                }
                // An unreachable API will not recover within the retry window; the next interaction retries.
                if (isServiceUnavailableError(error)) {
                    return false;
                }
                // Don't retry on 501 (Not Implemented) errors - these are DI container race conditions
                if (error instanceof Error && error.message.includes("501")) {
                    return false;
                }
                // Don't retry on 404 (Not Found) errors
                if (error instanceof Error && error.message.includes("404")) {
                    return false;
                }
                // For other errors, retry up to 2 times (reduced from 3)
                return failureCount < 2;
            },
            retryDelay: (failureCount, error) => isRetryablePendingError(error) ? error.retryAfterMs : Math.min(1_000 * 2 ** failureCount, 30_000),
            refetchOnWindowFocus: false, // Prevent unnecessary refetches
        },
        mutations: {
            retry: 1,
            onError: (error) => {
                const errorMessage = error instanceof Error ? error.message : JSON.stringify(error);
                console.error("Mutation failed:", error);
            },
        },
    },
    queryCache: new QueryCache({
        onError: (error, query) => {
            if (isRetryablePendingError(error)) {
                return;
            }
            const errorMessage = error instanceof Error ? error.message : JSON.stringify(error);
            if (query.meta?.suppressErrorToast === true) {
                console.error("Query failed:", error);
                return;
            }
            if (isServiceUnavailableError(error)) {
                console.debug("Query failed: service unavailable", query.queryKey);
                customToast.error(SERVICE_UNAVAILABLE_MESSAGE, { id: "service-unavailable" });
                return;
            }
            customToast.error(`Something went wrong: ${errorMessage}`);
        },
    }),
});
export default queryClient;
const reactQueryKeys = {
    // User-related queries
    USER: {
        ROOT: "user",
        CURRENT: "user.current",
        EMAIL: "user.email",
        PREFERENCES: {
            ROOT: "user.preferences",
            PUBLIC_CORPUS: "user.preferences.public-corpus",
        },
    },
    // Team-related queries
    TEAM: {
        ROOT: "team",
        ID: "team.id",
        ICON: "team.icon",
        USERS: "team.users",
        SUBSCRIPTION: "team.subscription",
    },
    // Document/File-related queries
    FILES: {
        ROOT: "files",
        ANALYSIS: "files.analysis",
        SFDT: "files.sfdt",
        TOKENS: "files.tokens",
        COUNT: "files.count",
        SCOREBOARD: "files.scoreboard",
        COHORT: "files.cohort",
        DETAIL: "files.detail",
        NAME: "files.name",
        RESPONSES: "files.responses",
        SECTIONS: {
            ROOT: "files.sections",
            NEW: "files.sections.new",
        },
        STATS: "files.stats",
        DOCUMENT_TYPES: {
            ALIGNED: "files.document-types.aligned",
            SCORES: "files.document-types.scores",
        },
        TYPE_ID: "files.type-id",
        FILE_SPANS: "files.file-spans",
    },
    // Document Types
    DOCUMENT_TYPES: {
        ROOT: "document-types",
        DATA: "document-types.data",
        ID: "document-types.id",
        STATS: "document-types.stats",
        LIST: "document-types.list",
        DETAIL: "document-types.detail",
    },
    // Document Analysis
    DOCUMENTS: {
        ROOT: "documents",
    },
    PREDICTIONS: "predictions",
    // Validation system
    VALIDATION: {
        ROOT: "validation",
        FILES: "validation.files",
        PREDICTIONS: "validation.predictions",
        NODES: "validation.nodes",
    },
    // Tree/Node structure
    TREES: {
        ROOT: "trees",
        NODES: "trees.nodes",
        LOAD_FILE_TYPE: "trees.load-file-type",
        ANCESTORS: "trees.ancestors",
        DESCENDENTS: "descendents",
    },
    // Nodes
    NODES: {
        ROOT: "nodes",
        LIST: "nodes.list",
        DETAIL: "nodes.detail",
        KEYS: "nodes.keys",
        FREQUENCIES: "nodes.frequencies",
    },
    // Corpus/Analysis
    CORPUS: {
        ROOT: "corpus",
        FILE_SCORES: "corpus.file-scores",
        SFDT_ALIGNED: "corpus.sfdt-aligned",
    },
    // Filtering and search
    FILTERS: {
        ROOT: "filters",
        LIST: "filters.list",
        OPTIONS: "filters.options",
        RECOMMENDED: "filters.recommended",
        LIST_OPTION_KEYS: "filters.list-option-keys",
    },
    PARTIES: "parties",
    MATTERS: {
        ROOT: "matters",
        NAMES: "matters.names",
        AND_FILES: {
            ROOT: "matters.files",
            FILTERS: "matters.files.filters",
        },
        DETAIL: "matter",
        TIMELINE: "matter-timeline",
    },
    RESPONSES: "responses",
    TEMPLATES: "templates",
    EVENTS: "events",
    INFERENCE_MODELS: "inference-models",
    CATEGORIES: "categories",
    // Work-related queries
    WORK: {
        ROOT: "work",
        BY_MATTER: "matter-work",
        BY_USER: "user-work",
        TEAM_USERS: "team-users",
    },
    // Dashboard queries
    DASHBOARD: {
        MATTERS: "dashboard-matters",
        COMPRESSED_AUDIT: "compressed-audit",
        AGENT_RECENT_CHATS: "dashboard-agent-recent-chats",
    },
    // Analytics queries
    ANALYTICS: {
        EFFECTIVE_DATE: "effectiveDate",
        FILTERS: "filters",
        CORPUS_DATES: "corpusDates",
        RADAR_DATA: "radarData",
    },
    // Data lake specific
    LABEL_STUDIO: {
        DOCUMENT_TYPE_ANALYTICS: "document-type-analytics",
        DOCUMENT_TYPE_VALIDATION_ANALYTICS: "document-type-validation-analytics",
        DOCUMENT_TYPE_STATS: "document-type-stats",
        FILES_WITH_STATS: "files-with-stats",
        ACTIVE_LABELS: "active-labels",
        PREDICTIONS: "predictions",
        ORIGINAL_DOWNLOAD: "original-download",
        DOCX_DOWNLOAD: "docx-download",
        STATISTICS: "statistics",
        AVAILABLE_TAGS: "available-tags",
    },
    CLUSTERS: {
        ROOT: "clusters",
    },
    // EDGAR views
    EDGAR: {
        ROOT: "edgar",
        FILINGS: "edgar.filings",
        STATS: "edgar.stats",
        CLAUSES: "edgar.clauses",
        AGGREGATIONS: "edgar.aggregations",
        SEARCH: "edgar.search",
    },
    // Chat-related queries
    CHAT: {
        ROOT: "chat",
        LIST: "chat.list",
        DETAIL: "chat.detail",
        MESSAGES: "chat.messages",
    },
};
// Query keys that are affected by the public corpus setting
export const CORPUS_DEPENDENT_QUERY_KEYS = [
    reactQueryKeys.FILES.ROOT,
    reactQueryKeys.FILES.TOKENS,
    reactQueryKeys.FILES.SCOREBOARD,
    reactQueryKeys.FILES.COHORT,
    reactQueryKeys.FILES.COUNT,
    reactQueryKeys.FILES.DOCUMENT_TYPES.SCORES,
    reactQueryKeys.FILES.DOCUMENT_TYPES.ALIGNED,
    reactQueryKeys.CORPUS.ROOT,
    reactQueryKeys.CORPUS.FILE_SCORES,
    reactQueryKeys.DOCUMENT_TYPES.ROOT,
    reactQueryKeys.DOCUMENT_TYPES.STATS,
    reactQueryKeys.VALIDATION.FILES,
    reactQueryKeys.MATTERS.AND_FILES.ROOT,
    reactQueryKeys.MATTERS.AND_FILES.FILTERS,
];
// React Query configuration constants
export const QUERY_CONFIGS = {
    // Fast-changing data (30s stale, 5m cache) - File history, live updates
    FAST_CHANGING: {
        staleTime: 30 * 1000,
        gcTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
    },
    // Standard data (5m stale, 10m cache) - Most common pattern
    STANDARD: {
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
        refetchOnWindowFocus: false,
    },
    // Analysis data (10m stale, 10m cache) - Document analysis, corpus data
    ANALYSIS: {
        staleTime: 10 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
        refetchOnWindowFocus: false,
    },
    // Long-term cache (5m stale, 5m cache, retry 3) - Labels, users
    LONG_TERM: {
        staleTime: 5 * 60 * 1000,
        gcTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
        retry: 3,
    },
    // Static data (24h stale, 24h cache) - Document type names, rarely changing data
    STATIC: {
        staleTime: 24 * 60 * 60 * 1000,
        gcTime: 24 * 60 * 60 * 1000,
        refetchOnWindowFocus: false,
    },
    // Real-time data (15s stale, refetch every 30s) - Live dashboards
    REAL_TIME: {
        staleTime: 15 * 1000,
        refetchInterval: 30 * 1000,
        refetchOnWindowFocus: false,
    },
    // Live work data (30s stale, refetch on focus) - Work tracking, team data
    LIVE_WORK_DATA: {
        staleTime: 30 * 1000,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
    },
    // No refetch (editing scenarios) - Document editing, form data
    NO_REFETCH: {
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        refetchInterval: false,
    },
    // Never stale (active editing) - Currently editing documents
    NEVER_STALE: {
        staleTime: Infinity,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
    },
    // External API (high cache, low refetch) - Search APIs, external services
    EXTERNAL_API: {
        staleTime: 10 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        refetchInterval: 10 * 60 * 1000,
    },
    // Autocomplete/Search (medium cache, good for typeahead)
    AUTOCOMPLETE: {
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
        refetchOnWindowFocus: false,
        retry: 1,
    },
};
// Mutation configuration constants
export const MUTATION_CONFIGS = {
    // Simple mutation with success notification
    SIMPLE: {
        onError: (error) => {
            console.error("Mutation failed:", error);
        },
    },
    // Optimistic update pattern
    OPTIMISTIC: {
        // Used with custom onMutate, onError, onSuccess, onSettled
        retry: 1,
    },
    // Cache update pattern
    CACHE_UPDATE: {
        // Used with custom onSuccess that updates cache
        retry: 1,
    },
};
export { reactQueryKeys };
